import React, { useEffect, useState } from 'react';
import { Platform } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { createPortal } from 'react-dom';
import { colores } from '../../../../styles/variablesCss';
import { useCart } from '../../../../context/CartContext';

export default function ScrollToTopButton({ onScrollToTop }) {
  const [mounted, setMounted] = useState(false);
  const [isAllowedRoute, setIsAllowedRoute] = useState(false);
  const [isGridVisible, setIsGridVisible] = useState(true);
  const [pressed, setPressed] = useState(false);
  const { carritoVisible } = useCart();

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') return;
    if (typeof window === 'undefined') return;

    const computeAllowed = () => {
      const path = window.location?.pathname || '';
      // Permitimos /productos y cualquier subruta (por si usan /productos/..)
      return path === '/productos' || path.startsWith('/productos/');
    };

    const sync = () => {
      const allowed = computeAllowed();
      setIsAllowedRoute(allowed);
      setMounted(true);
    };

    // Sync inicial
    sync();

    // Escuchar cambios de navegación en web
    window.addEventListener('popstate', sync);
    window.addEventListener('navigationChange', sync);

    return () => {
      window.removeEventListener('popstate', sync);
      window.removeEventListener('navigationChange', sync);
    };
  }, []);

  // Mostrar el botón solo cuando la grilla de productos está visible (en viewport)
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined' || typeof document === 'undefined') return;

    let observer;
    const setup = () => {
      try {
        const el = document.getElementById('productosFlatList');
        if (!el) {
          setIsGridVisible(false);
          return;
        }

        // Si no hay IntersectionObserver, fallback: si existe el nodo asumimos visible
        if (typeof window.IntersectionObserver !== 'function') {
          setIsGridVisible(true);
          return;
        }

        observer = new window.IntersectionObserver(
          (entries) => {
            const entry = entries && entries[0];
            const visible = !!entry && (entry.isIntersecting || (entry.intersectionRatio || 0) > 0);
            setIsGridVisible(visible);
          },
          { root: null, threshold: [0, 0.01, 0.1] }
        );

        observer.observe(el);
      } catch (_) {
        setIsGridVisible(true);
      }
    };

    setup();

    // Reintentar cuando cambia la navegación (por si el nodo se monta/desmonta)
    window.addEventListener('navigationChange', setup);

    return () => {
      window.removeEventListener('navigationChange', setup);
      try {
        if (observer) observer.disconnect();
      } catch (_) {}
    };
  }, []);

  const scrollToTop = () => {
    try {
      if (typeof onScrollToTop === 'function') {
        onScrollToTop();
      }
    } catch (_) {}

    // En web, a veces el scroll real está en un contenedor (no en window)
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      try {
        const root = document.getElementById('productosFlatList');

        const isScrollable = (node) => {
          if (!node) return false;
          const style = window.getComputedStyle(node);
          const overflowY = style?.overflowY;
          const canScroll = overflowY === 'auto' || overflowY === 'scroll';
          return canScroll && node.scrollHeight > node.clientHeight;
        };

        let candidate = root;
        while (candidate && candidate !== document.body && !isScrollable(candidate)) {
          candidate = candidate.parentElement;
        }

        const target = isScrollable(candidate)
          ? candidate
          : (document.scrollingElement || document.documentElement || document.body);

        if (target && typeof target.scrollTo === 'function') {
          target.scrollTo({ top: 0, behavior: 'smooth' });
        } else if (target) {
          target.scrollTop = 0;
        }
      } catch (_) {}
    }

    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      try {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (_) {
        window.scrollTo(0, 0);
      }

      // Fallback extra para navegadores/containers particulares
      try {
        if (document?.documentElement) document.documentElement.scrollTop = 0;
        if (document?.body) document.body.scrollTop = 0;
      } catch (_) {}
    }
  };

  if (Platform.OS !== 'web') return null;
  if (!mounted || !isAllowedRoute || !isGridVisible) return null;
  if (carritoVisible) return null; // Ocultar cuando el carrito está abierto

  const node = typeof document !== 'undefined' ? document.body : null;
  const content = (
    <div
      onPointerDown={() => {
        setPressed(true);
        scrollToTop();
      }}
      onPointerUp={() => setPressed(false)}
      onPointerCancel={() => setPressed(false)}
      onMouseLeave={() => setPressed(false)}
      onTouchEnd={() => setPressed(false)}
      style={{
        position: 'fixed',
        bottom: '30px',
        right: '30px',
        width: '50px',
        height: '50px',
        borderRadius: '25px',
        backgroundColor: '#ff000080' ,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        cursor: 'pointer',
        zIndex: 500,
        boxShadow: '0 4px 12px hsl(0, 0%, 0%) ',
        pointerEvents: 'auto',
        userSelect: 'none',
        touchAction: 'manipulation',
        transform: pressed ? 'scale(0.92)' : 'scale(1)',
        transition: 'transform 120ms ease',
      }}
    >
      <div style={{ pointerEvents: 'none', display: 'flex' }}>
        <MaterialIcons name="arrow-upward" size={24} color="#ffffff" />
      </div>
    </div>
  );

  if (!node) return null;
  return createPortal(content, node);
}
